import React from 'react';
import { Box, Dialog, Divider, IconButton, Slide } from '@mui/material';
import { ModalTitle } from '../Modal';
import { useMessagingContext } from './MessagingProvider.component';
import { ConversationsList } from './ConversationsList.component';
import { Color } from './types';
import { Messages } from './Messages';
import { useUserConversationsQuery } from '@uplift/queries';
import { useIsMobile } from '@uplift/hooks';
import { ArrowBack } from '@mui/icons-material';


export type MessagingModalProps = {
  color?: Color;
};


const MobileMessagingModal: React.FC<MessagingModalProps> = ({ color = 'secondary' }) => {
  const messaging = useMessagingContext();
  const { data: conversations = [] } = useUserConversationsQuery({ enabled: messaging.modalOpened });
  const selectedConversation = React.useMemo(() => conversations.find(c => c.id === messaging.selectedConversationId), [ conversations, messaging.selectedConversationId ]);

  return (
    <Dialog
      data-testid="messaging-modal"
      open={messaging.modalOpened}
      onClose={messaging.closeModal}
      fullScreen
      slots={{ transition: Slide }}
      slotProps={{
        transition: {
          direction: 'up',
          onExited: () => messaging.setSelectedConversationId(undefined),
        },
      }}
    >
      <ModalTitle onClose={messaging.closeModal}>
        <IconButton size="small" sx={{ opacity: selectedConversation ? 1 : 0 }} onClick={() => messaging.setSelectedConversationId(undefined)}><ArrowBack /></IconButton>
        Messages
      </ModalTitle>
      {selectedConversation ? (
        <Messages conversation={selectedConversation} color={color} />
      ) : (
        <ConversationsList
          selectedConversationId={messaging.selectedConversationId}
          setSelectedConversationId={messaging.setSelectedConversationId}
          color={color}
        />
      )}
    </Dialog>
  );
};


const DesktopMessagingModal: React.FC<MessagingModalProps> = ({ color = 'secondary' }) => {
  const messaging = useMessagingContext();
  const { data: conversations = [] } = useUserConversationsQuery({ enabled: messaging.modalOpened });
  const selectedConversation = React.useMemo(() => conversations.find(c => c.id === messaging.selectedConversationId), [ conversations, messaging.selectedConversationId ]);

  return (
    <Dialog
      data-testid="messaging-modal"
      open={messaging.modalOpened}
      onClose={messaging.closeModal}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            maxHeight: '80%',
            height: '100%',
            minHeight: '70%',
          }
        },
        transition: {
          onExited: () => messaging.setSelectedConversationId(undefined),
        }
      }}
    >
      <ModalTitle onClose={messaging.closeModal}>Messages</ModalTitle>
      <Divider />
      <Box display="flex" height="100%" width="100%">
        <Box flex={1} maxWidth="360px">
          <ConversationsList
            selectedConversationId={messaging.selectedConversationId}
            setSelectedConversationId={messaging.setSelectedConversationId}
            color={color}
          />
        </Box>
        <Divider orientation="vertical" />
        <Messages conversation={selectedConversation} color={color} />
      </Box>
    </Dialog>
  );
};


export const MessagingModal: React.FC<MessagingModalProps> = ({ color = 'secondary' }) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return <MobileMessagingModal color={color} />;
  }

  return <DesktopMessagingModal color={color} />;
};
